import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useToast } from "@forge/ui";
import { api } from "../utils/api";
import "./SettingsPage.css";

type Profile = {
  id: string;
  email: string;
  name: string;
  role: string;
  avatar_url: string | null;
  ftp: number | null;
  threshold_hr: number | null;
  max_hr: number | null;
  created_at: number;
};

type StravaStatus = {
  connected: boolean;
  athlete_id?: string;
  athlete_name?: string;
  last_sync?: number;
};

export function SettingsPage() {
  const { show } = useToast();
  const [params, setParams] = useSearchParams();

  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [ftp, setFtp] = useState("");
  const [thresholdHr, setThresholdHr] = useState("");
  const [maxHr, setMaxHr] = useState("");
  const [savingZones, setSavingZones] = useState(false);

  const [strava, setStrava] = useState<StravaStatus | null>(null);
  const [stravaLoading, setStravaLoading] = useState(false);

  useEffect(() => {
    const stravaParam = params.get("strava");
    if (stravaParam === "connected") {
      show("Strava connected!", "success");
      setParams({}, { replace: true });
    }
    const googleParam = params.get("google");
    if (googleParam === "connected") {
      show("Google account linked!", "success");
      setParams({}, { replace: true });
    }
  }, [params, setParams, show]);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const [p, s] = await Promise.all([
          api.get<Profile>("/api/user/profile"),
          api.get<StravaStatus>("/api/user/integrations/strava/status"),
        ]);
        if (!alive) return;
        setProfile(p);
        setFtp(p.ftp != null ? String(p.ftp) : "");
        setThresholdHr(p.threshold_hr != null ? String(p.threshold_hr) : "");
        setMaxHr(p.max_hr != null ? String(p.max_hr) : "");
        setStrava(s);
      } catch (e: unknown) {
        if (alive) setError(e instanceof Error ? e.message : "Failed to load settings");
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, []);

  async function saveZones(e: React.FormEvent) {
    e.preventDefault();
    setSavingZones(true);
    try {
      const body: Record<string, unknown> = {};
      if (ftp.trim() !== "") body.ftp = parseFloat(ftp);
      else body.ftp = null;
      if (thresholdHr.trim() !== "") body.threshold_hr = parseFloat(thresholdHr);
      else body.threshold_hr = null;
      if (maxHr.trim() !== "") body.max_hr = parseFloat(maxHr);
      else body.max_hr = null;
      const updated = await api.put<Profile>("/api/user/profile", body);
      setProfile(updated);
      show("Training zones saved", "success");
    } catch (e: unknown) {
      show(e instanceof Error ? e.message : "Failed to save", "error");
    } finally {
      setSavingZones(false);
    }
  }

  async function handleStravaConnect() {
    setStravaLoading(true);
    try {
      const res = await api.get<{ url: string }>("/api/user/integrations/strava/connect?json=1");
      window.location.assign(res.url);
    } catch (e: unknown) {
      show(e instanceof Error ? e.message : "Failed to connect Strava", "error");
      setStravaLoading(false);
    }
  }

  async function handleStravaSync() {
    setStravaLoading(true);
    try {
      const res = await api.post<{ imported: number; skipped: number }>("/api/user/integrations/strava/sync", {});
      show(`Synced ${res.imported} activities (${res.skipped} skipped)`, "success");
      const s = await api.get<StravaStatus>("/api/user/integrations/strava/status");
      setStrava(s);
    } catch (e: unknown) {
      show(e instanceof Error ? e.message : "Sync failed", "error");
    } finally {
      setStravaLoading(false);
    }
  }

  async function handleStravaDisconnect() {
    setStravaLoading(true);
    try {
      await api.post<unknown>("/api/user/integrations/strava/disconnect", {});
      setStrava({ connected: false });
      show("Strava disconnected", "success");
    } catch (e: unknown) {
      show(e instanceof Error ? e.message : "Disconnect failed", "error");
    } finally {
      setStravaLoading(false);
    }
  }

  if (loading) {
    return <div className="settings-page"><p className="settings-muted">Loading settings...</p></div>;
  }

  if (error) {
    return <div className="settings-page"><div className="settings-error">{error}</div></div>;
  }

  return (
    <div className="settings-page">
      <h1>Settings</h1>

      <section className="settings-card">
        <h2 className="settings-card-title">Profile</h2>
        <div className="settings-field">
          <label className="settings-label">Name</label>
          <div className="settings-readonly">{profile?.name || "—"}</div>
        </div>
        <div className="settings-field">
          <label className="settings-label">Email</label>
          <div className="settings-readonly">{profile?.email || "—"}</div>
        </div>
      </section>

      <section className="settings-card">
        <h2 className="settings-card-title">Training Zones</h2>
        <form onSubmit={saveZones} className="settings-form">
          <div className="settings-field">
            <label className="settings-label" htmlFor="ftp">FTP (watts)</label>
            <input
              id="ftp"
              type="number"
              step="1"
              className="settings-input"
              value={ftp}
              onChange={(e) => setFtp(e.target.value)}
              placeholder="e.g. 250"
            />
          </div>
          <div className="settings-field">
            <label className="settings-label" htmlFor="threshold-hr">Threshold HR (bpm)</label>
            <input
              id="threshold-hr"
              type="number"
              step="1"
              className="settings-input"
              value={thresholdHr}
              onChange={(e) => setThresholdHr(e.target.value)}
              placeholder="e.g. 165"
            />
          </div>
          <div className="settings-field">
            <label className="settings-label" htmlFor="max-hr">Max HR (bpm)</label>
            <input
              id="max-hr"
              type="number"
              step="1"
              className="settings-input"
              value={maxHr}
              onChange={(e) => setMaxHr(e.target.value)}
              placeholder="e.g. 195"
            />
          </div>
          <button type="submit" className="settings-btn-primary" disabled={savingZones}>
            {savingZones ? "Saving..." : "Save Zones"}
          </button>
        </form>
      </section>

      <section className="settings-card">
        <h2 className="settings-card-title">Strava</h2>
        {strava?.connected ? (
          <div className="strava-connected">
            <p className="strava-status-text">
              Connected{strava.athlete_name ? ` as ${strava.athlete_name}` : ""}
            </p>
            {strava.last_sync && (
              <p className="strava-last-sync">
                Last sync: {new Date(strava.last_sync * 1000).toLocaleDateString()}
              </p>
            )}
            <div className="strava-actions">
              <button
                className="settings-btn-primary"
                onClick={handleStravaSync}
                disabled={stravaLoading}
              >
                {stravaLoading ? "Syncing..." : "Sync Activities"}
              </button>
              <button
                className="settings-btn-danger"
                onClick={handleStravaDisconnect}
                disabled={stravaLoading}
              >
                Disconnect
              </button>
            </div>
          </div>
        ) : (
          <div className="strava-disconnected">
            <p className="strava-status-text">Not connected</p>
            <button
              className="settings-btn-strava"
              onClick={handleStravaConnect}
              disabled={stravaLoading}
            >
              {stravaLoading ? "Connecting..." : "Connect Strava"}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

export default SettingsPage;
